import { useParams } from 'react-router-dom';
import { Button, Flex, Skeleton, Text, useToast } from '@chakra-ui/react';
import { PageContent } from '@/components/PageContent';
import { SectionBox } from '@/components/SectionBox';
import { breadcrumb } from './breadcrumb';
import { useFinancialMonthlyPaymentById, useFinancialMonthlyPaymentExportByIdMutation } from '@/services/queries/financial';
import { ExportType, FinancialTransactionType, PaymentMethod } from '@/model';
import { formatDateToDateDisplay } from '@/utils/formatDateToDateDisplay';
import { formatDateToTimeDisplay } from '@/utils/formatDateToTimeDisplay';
import { formatDocument } from '@/utils/formatDocument';
import { downloadExport, formatCurrency } from '@/utils';
import { useTranslation } from 'react-i18next';

const textByFinancialType = {
  [FinancialTransactionType.MONTHLY_PAYMENT]: 'financial_transaction_detail_monthly_payment',
  [FinancialTransactionType.EXPERIENCE]: 'financial_transaction_detail_experience',
};

const textByPaymentMethod = {
  [PaymentMethod.CREDIT_CARD]: 'financial_transaction_detail_payment_method_credit_card',
  [PaymentMethod.PIX]: 'financial_transaction_detail_payment_method_pix',
};

const FinancialTransactionDetailSkeleton = () => {
  return (
    <>
      <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
        <Skeleton w={{ md: '160px' }} h='21px' />
        <Skeleton w={{ md: '100px' }} h='21px' />
      </Flex>

      <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
        <Skeleton w={{ md: '160px' }} h='21px' />

        <Flex direction='column' gap='0.075rem'>
          <Skeleton w={{ md: '100px' }} h='21px' />
          <Skeleton w={{ md: '70px' }} h='21px' />
        </Flex>
      </Flex>

      <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
        <Skeleton w={{ md: '160px' }} h='21px' />

        <Flex direction='column' gap='0.075rem'>
          <Skeleton w={{ md: '100px' }} h='21px' />
          <Skeleton w={{ md: '70px' }} h='21px' />
        </Flex>
      </Flex>

      <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
        <Skeleton w={{ md: '160px' }} h='21px' />
        <Skeleton w={{ md: '100px' }} h='21px' />
      </Flex>

      <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
        <Skeleton w={{ md: '160px' }} h='21px' />
        <Skeleton w={{ md: '100px' }} h='21px' />
      </Flex>

      <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
        <Skeleton w={{ md: '160px' }} h='21px' />
        <Skeleton w={{ md: '100px' }} h='21px' />
      </Flex>

      <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
        <Skeleton w={{ md: '160px' }} h='21px' />
        <Skeleton w={{ md: '100px' }} h='21px' />
      </Flex>

      <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
        <Skeleton w={{ md: '160px' }} h='21px' />
        <Skeleton w={{ md: '100px' }} h='21px' />
      </Flex>
    </>
  );
};

export const FinancialTransactionDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const toast = useToast();
  const { data: itemData, isLoading } = useFinancialMonthlyPaymentById(id as string);
  const exportMutation = useFinancialMonthlyPaymentExportByIdMutation();

  const handleDownload = () => {
    exportMutation.mutate(id as string, {
      onSuccess: (data: any) => {
        downloadExport(data, 'transação', ExportType.XLSX);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.financial.financial_transaction_detail_export_error'),
        });
      },
    });
  };

  return (
    <PageContent title={t('pages.financial.financial_transaction_detail_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction='column' gap={3}>
          {isLoading ? (
            <FinancialTransactionDetailSkeleton />
          ) : (
            <>
              <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
                <Text w={{ md: '160px' }}>{t('pages.financial.financial_transaction_detail_type_label')}</Text>
                <Text fontWeight='bold'>{t(`pages.financial.${textByFinancialType[itemData?.transaction_type as FinancialTransactionType]}`)}</Text>
              </Flex>

              <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
                <Text w={{ md: '160px' }}>{t('pages.financial.financial_transaction_detail_date_label')}</Text>
                <Flex direction='column' gap='0.075rem'>
                  <Text fontWeight='bold'>{itemData?.charged_at ? formatDateToDateDisplay(itemData?.charged_at) : '-'}</Text>
                  <Text fontWeight='bold'>{itemData?.charged_at ? formatDateToTimeDisplay(itemData?.charged_at) : '-'}</Text>
                </Flex>
              </Flex>

              <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
                <Text w={{ md: '160px' }}>{t('pages.financial.financial_transaction_detail_subscriber_label')}</Text>
                <Flex direction='column' gap='0.075rem'>
                  <Text fontWeight='bold'>{itemData?.subscriber_name}</Text>
                  <Text fontWeight='bold'>
                    {itemData?.subscriber_document ? formatDocument(itemData?.subscriber_document) : '-'}
                  </Text>
                </Flex>
              </Flex>

              <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
                <Text w={{ md: '160px' }}>{t('pages.financial.financial_transaction_detail_plan_label')}</Text>
                <Text fontWeight='bold'>{itemData?.subscription_plan}</Text>
              </Flex>

              <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
                <Text w={{ md: '160px' }}>{t('pages.financial.financial_transaction_detail_value_label')}</Text>
                <Text fontWeight='bold'>{itemData?.price ? formatCurrency(itemData?.price) : '-'}</Text>
              </Flex>

              <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
                <Text w={{ md: '160px' }}>{t('pages.financial.financial_transaction_detail_payment_method_label')}</Text>
                <Text fontWeight='bold'>{itemData?.payment_method ? t(`pages.financial.${textByPaymentMethod[itemData?.payment_method]}`) : '-'}</Text>
              </Flex>

              <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
                <Text w={{ md: '160px' }}>{t('pages.financial.financial_transaction_detail_payment_method_label')}</Text>
                <Text fontWeight='bold'>{itemData?.card_final_numbers}</Text>
              </Flex>

              <Flex gap={1} direction={{ base: 'column', md: 'row' }}>
                <Text w={{ md: '160px' }}>{t('pages.financial.financial_transaction_detail_transaction_id_label')}</Text>
                <Text fontWeight='bold'>{itemData?.id}</Text>
              </Flex>
            </>
          )}
        </Flex>

        {isLoading ? (
          <Skeleton w='156px' h='40px' />
        ) : (
          <Button
            alignSelf='flex-start'
            variant='outlined'
            color='gray.600'
            onClick={handleDownload}
            isLoading={exportMutation.isLoading}
          >
            {t('pages.financial.financial_transaction_detail_download_receipt')}
          </Button>
        )}
      </SectionBox>
    </PageContent>
  );
};